/* eslint-disable no-underscore-dangle */
import React, {
  useState,
  useEffect,
} from 'react';
import i18n from 'i18next';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  useNavigate,
  useLocation,
} from 'react-router';
import {
  createFilter,
} from 'react-select';

import {
  WorkoutType,
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  State,
} from 'web-types';

import {
  useClickAway,
} from 'web-hooks';
import {
  getUiState,
} from 'web-selectors';
import {
  uiActions,
} from 'web-actions';

import {
  Button,
  Select,
  Tooltip,
  AddExercise,
  AssignSearchOption,
} from 'web-components';

import {
  Flex,
  SVG,
} from 'web-styled';

import * as S from './styled';

interface Props {
  workoutId: number,
  workoutType: WorkoutType,
  onWorkoutChange?: () => void,
}

const mapState = (state: State) => {
  const exercises = coreSelectors.getEntitySearchOptions(
    'exercises',
    'library',
  )(state);

  return {
    options: [
      {
        newOne: true,
        value: 'new',
        label: 'CREATE NEW',
      },
      ...exercises,
    ],
    exerciseFolders: coreSelectors.getEntityFolders(
      'exercises',
    )(state),
    ...getUiState([
      'openedTooltips',
      'exercisePickerIsOpen',
    ])(state),
  };
};

const WorkoutExercisesFooter = ({
  workoutId,
  workoutType,
  onWorkoutChange,
}: Props) => {
  const [visible, setVisible] = useState(false);
  const [newExerciseModal, setNewExerciseModal] = useState({
    name: '',
    isOpen: false,
  });

  const {
    options,
    openedTooltips,
    exerciseFolders,
    exercisePickerIsOpen,
  } = useSelector(mapState, shallowEqual);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const ref = useClickAway(
    () => {
      setVisible(false);
      dispatch(uiActions.cancelExercisePicker());
    },
    {
      disabled: !visible,
    },
  );

  const onRequestAdd = (exerciseId: number) => {
    dispatch(
      coreActions.requestAddExerciseToWorkout({
        workoutId,
        exerciseId,
        workoutType,
        callbacks: {
          onSuccess: () => {
            onWorkoutChange?.();
            const button = document.querySelector('.exercise-picker-button');
            button?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          },
        },
      }),
    );
  };

  useEffect(() => {
    if (exercisePickerIsOpen && !visible) {
      setVisible(true);
    }
    if (!exercisePickerIsOpen && visible) {
      setVisible(false);
    }
  }, [exercisePickerIsOpen]);

  useEffect(() => {
    const entity = location.state?.entity;

    if (entity?.id) {
      if (exercisePickerIsOpen) {
        dispatch(uiActions.selectExercisePicker(entity.id));
      } else {
        onRequestAdd(entity.id);
      }

      navigate(location.pathname, { replace: true });
    }
  }, [location]);

  const handleAddNewExercise = (payload: any, actions: any) => {
    const onSuccess = (res: any) => {
      setNewExerciseModal({
        name: '',
        isOpen: false,
      });

      navigate(`/exercises/${res.id}/edit?back=${location.pathname}`);
    };

    dispatch(
      coreActions.requestAddEntity({
        payload,
        resourceType: 'exercises',
        list: 'library',
        callbacks: {
          ...actions,
          onSuccess,
        },
      }),
    );
  };

  const hasTooltip = openedTooltips.some(el => el.key === 'add_workout_exercise');

  return (
    <S.Footer>
      <Flex flex="0 0 260px">
        {visible ? (
          <S.Search ref={ref}>
            <Select
              value={null}
              autoFocus
              menuIsOpen
              creatable
              placeholder="Search exercises"
              menuPlacement="top"
              createOptionPosition="first"
              filterOption={createFilter({ ignoreAccents: false })}
              components={{ Option: AssignSearchOption }}
              formatCreateLabel={(userInput: string) => (
                <S.Label>
                  Add
                  {' '}
                  <b>{userInput}</b>
                </S.Label>
              )}
              options={options}
              styles={{
                menu: (provided: any) => ({
                  ...provided,
                  marginTop: '1px',
                }),
                menuList: (provided: any) => ({
                  ...provided,
                  maxHeight: '220px',
                }),
              }}
              onChange={(option: any) => {
                setVisible(false);
                if (!option) return;

                if (option.__isNew__ || option.newOne) {
                  setNewExerciseModal({
                    name: option.newOne ? '' : option.value,
                    isOpen: true,
                  });

                  return;
                }

                if (exercisePickerIsOpen) {
                  dispatch(uiActions.selectExercisePicker(+option.value));
                } else {
                  onRequestAdd(+option.value);
                }
              }}
            />
          </S.Search>
        ) : (
          <Tooltip
            body={hasTooltip
              ? 'Click here whenever you want to add an exercise.'
              : i18n.t('tooltip.add.workout.exercise')}
            isOpen={hasTooltip}
            positions={['right']}
          >
            <Button
              noPrint
              icon={<SVG.Plus size={13} strokeWidth={3} />}
              appearance="success"
              className="exercise-picker-button"
              onClick={() => {
                setTimeout(() => setVisible(true), 0);
                if (hasTooltip) {
                  dispatch(
                    uiActions.setUiState({
                      openedTooltips: openedTooltips.filter(el => el.key !== 'add_workout_exercise'),
                    }),
                  );
                }
              }}
            >
              Add Exercise
            </Button>
          </Tooltip>
        )}
      </Flex>

      <AddExercise
        isOpen={newExerciseModal.isOpen}
        initialName={newExerciseModal.name}
        folders={exerciseFolders}
        onSubmit={handleAddNewExercise}
        onRequestClose={() => {
          setNewExerciseModal({
            name: '',
            isOpen: false,
          });
        }}
      />
    </S.Footer>
  );
};

export default WorkoutExercisesFooter;
